import React, { useState, useCallback, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import PropTypes from 'prop-types';

// Components
import HeaderSection from '../../../components/layout/header';
import CustomDropdown from './CustomDropdown';
import FileUpload from './FileUpload';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormControlWithFocus,
  FormMessage,
  FormDescription,
  useFormValidation,
} from "../../../shared/components/form";

// Utils and Services
import { uploadFileToS3, getContentType } from '../../../shared/utils/fileOperations';
import { getPresignedUrlWithoutAuth } from '../../../shared/actions/fileActions';
import { validateFiles } from '../../../shared/utils/fileValidation';
import { submitContactForm } from '../../../shared/actions/helpAction';
import { showError } from '../../../shared/utils/swalHandler';
import { showManualSuccessPushToast } from '../../../shared/pusNotification/useManualToasts';

// Constants and Validation
import { MAX_FILE_SIZE } from '../../../shared/constants/valisdation';
import { CONFIG, CONTACT_US_INFO } from '../../../shared/constants/common';
import { createContactFormSchema } from "../../../shared/validations/contact";
import { formatProfileTag } from '../../../shared/utils/function';

// Styles
import './contact-us.css';

const ModernContactUs = () => {
  const userInfo = useSelector((state) => state.user?.user);

  const form = useForm({
    resolver: zodResolver(createContactFormSchema()),
    defaultValues: {
      feedbackReason: '',
      subject: '',
      message: '',
      email: userInfo?.email || '',
      attachments: []
    },
    mode: "onChange",
  });

  const getFeedbackReason = form.watch('feedbackReason') ? formatProfileTag(form.watch('feedbackReason')) + ' - ' : '';

  // State to track user-entered subject (without prefix)
  const [userSubject, setUserSubject] = useState('');

  // Add the validation hook with focus functionality
  const {
    validateAndFocus,
  } = useFormValidation(form, {
    showErrorsOnSubmit: true,
    focusOnFirstError: true,
  });

  // Extract user input when feedback reason changes
  useEffect(() => {
    const currentSubject = form.getValues("subject") || "";
    if (getFeedbackReason) {
      // If prefix exists, extract user part
      if (currentSubject.startsWith(getFeedbackReason)) {
        const userPart = currentSubject.substring(getFeedbackReason.length).trim();
        setUserSubject(userPart);
      } else {
        // If subject doesn't start with prefix, clear user input
        setUserSubject("");
      }
    } else {
      // If no prefix, use entire subject as user input
      setUserSubject(currentSubject);
    }
  }, [getFeedbackReason, form]);

  // Update form subject when user input changes
  useEffect(() => {
    if (getFeedbackReason) {
      const fullSubject = userSubject ? `${getFeedbackReason} ${userSubject}`.trim() : getFeedbackReason;
      form.setValue("subject", fullSubject, { shouldValidate: false, shouldDirty: true });
    } else {
      form.setValue("subject", userSubject, { shouldValidate: false, shouldDirty: true });
    }
  }, [userSubject, getFeedbackReason, form]);

  // Set email from user info if logged in and email field is empty
  useEffect(() => {
    if (userInfo?.email) {
      const currentEmail = form.getValues("email");
      // Only set email if field is empty (allows user to edit if they've already typed)
      if (!currentEmail || currentEmail.trim() === '') {
        form.setValue("email", userInfo.email, { shouldValidate: false, shouldDirty: false });
      }
    }
  }, [userInfo, form]);

  const getCompanyInfo = useCallback(() => {
    const feedbackReason = form.watch('feedbackReason');
    const companyText = `${CONTACT_US_INFO.name}, ${CONTACT_US_INFO.location}`;

    switch (feedbackReason) {
      case 'feedback':
      case 'report':
      case 'general-help':
      case 'verification-help':
      case 'dmca-help':
      case 'payout-help':
      case 'referral-help':
        return (
          <div className="modern-contact-company-info">
            <p className="modern-contact-company-text">{companyText}</p>
          </div>
        );

      case 'appeal':
        return (
          <div className="modern-contact-company-info">
            <p className="modern-contact-company-text">{companyText}</p>
            <div className="modern-contact-appeal-info">
              <p className="modern-contact-appeal-title">Appeals:</p>
              <p className="modern-contact-appeal-text">
                If you have been depicted in any content and would like to appeal removal of such content,
                please notify us by email at{' '}
                <span className="modern-contact-email-link">
                  {CONFIG.APPEAL_EMAIL}
                </span>.
              </p>
            </div>
          </div>
        );

      case 'complaint':
        return (
          <div className="modern-contact-company-info">
            <p className="modern-contact-company-text">{companyText}</p>
            <div className="modern-contact-complaint-info">
              <p className="modern-contact-complaint-title">Complaints:</p>
              <p className="modern-contact-complaint-text">
                For all complaints, please contact us via email at{' '}
                <span className="modern-contact-email-link">
                  {CONFIG.COMPLAINT_EMAIL}
                </span>.
              </p>
            </div>
          </div>
        );

      case 'billing':
        return (
          <div className="modern-contact-company-info">
            <p className="modern-contact-company-text">{companyText}</p>
            <div className="modern-contact-billing-info">
              <p className="modern-contact-billing-text">
                <div>For billing inquiries, you can also contact CCBill directly:</div>
                <a
                  className="modern-contact-email-link"
                  href={CONTACT_US_INFO.ccbill}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {CONTACT_US_INFO.ccbill}
                </a>
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  }, [form]);

  // File upload state
  const [fileState, setFileState] = useState({
    attachments: [],
    uploadedUrls: [],
    isUploading: false,
  });


  const handleFileUpload = useCallback(async (files) => {
    if (!files || files.length === 0) return;

    setFileState(prev => ({ ...prev, isUploading: true }));
    const uploadedUrls = [];

    try {
      const validatedFiles = await validateFiles(files, MAX_FILE_SIZE.CONTACT_US);
      if (!validatedFiles || validatedFiles.length === 0) return;

      // Only process and preview validated files
      for (const file of validatedFiles) {
        // Get presigned URL
        const presignedResult = await getPresignedUrlWithoutAuth(file, false, 'contact');

        if (!presignedResult.success || !presignedResult.data || !presignedResult.data.url) {
          throw new Error(presignedResult.error || 'Failed to get upload URL');
        }

        const { url: presignedUrl, key } = presignedResult.data;

        // Upload file to S3 using presigned URL
        const contentType = getContentType(file);

        const uploadedUrl = await uploadFileToS3(
          presignedUrl,
          file,
          contentType,
          undefined,
          true
        );
        uploadedUrls.push({
          name: file.name,
          url: uploadedUrl,
          key: key
        });
      }

      // Only add validated files to state for preview and upload
      setFileState(prev => ({
        ...prev,
        uploadedUrls: [...prev.uploadedUrls, ...uploadedUrls],
        attachments: [...prev.attachments, ...validatedFiles],
      }));
    } catch (error) {
      await showError('Upload Error', error.message || 'Failed to upload files. Please try again.');
    } finally {
      setFileState(prev => ({ ...prev, isUploading: false }));
    }
  }, []);

  const handleRemoveFile = useCallback((index) => {
    setFileState(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index),
      uploadedUrls: prev.uploadedUrls.filter((_, i) => i !== index),
    }));
  }, []);

  const onSubmit = useCallback(async (data) => {
    try {
      let requestBody = {
        ...(userInfo?.username && { 
          name: userInfo?.username,
          role: userInfo?.rol,
        }),
        email: data.email,
        reason: data.feedbackReason,
        subject: data.subject,
        message: data.message,
      };
      
      // Add phone parameter if user is logged in and has phone information
      if (userInfo && userInfo.phone_country_code && userInfo.phone_number) {
        const phone = `${userInfo.phone_country_code}${userInfo.phone_number}`;
        requestBody.phone = phone;
      }
      
      // Only include file keys if files were uploaded
      if (fileState.uploadedUrls && fileState.uploadedUrls.length > 0) {
        requestBody.file = fileState.uploadedUrls.map(file => file.key).join(',');
      }

      const result = await submitContactForm(requestBody);
      if (result.success) {
        showManualSuccessPushToast(
          'Request Submitted',
          'Thank you for contacting us. We will get back to you as soon as possible.',
          userInfo
        );
        form.reset();
        setUserSubject('');
        setFileState({
          attachments: [],
          uploadedUrls: [],
          isUploading: false,
        });
      } else {
        throw new Error(result?.error || 'Failed to submit request');
      }
    } catch (error) {
      await showError('Error', error?.message || 'Failed to submit your request. Please try again.');
    }
  }, [fileState.uploadedUrls, form, userInfo]);

  return (
    <div className={`modern-contact-page`}>
      {/* Header */}
      <HeaderSection
        type="text"
        title="Contact Support"
      />

      {/* Main Content */}
      <main className="modern-contact-main">
        <div className="modern-contact-container">
          <Form {...form}>
            <form
              className="modern-contact-form"
              onSubmit={form.handleSubmit(async (data) => {
                const isValid = await validateAndFocus();
                if (!isValid) {
                  // Form has validation errors, they will be shown and focused
                  return;
                }

                // If validation passes, proceed with submission
                await onSubmit(data);
              })}>
              {/* Feedback Reason Section */}
              <FormField
                control={form.control}
                name="feedbackReason"
                render={({ field }) => (
                  <FormItem className="modern-contact-section cm_z_10">
                    <FormControlWithFocus fieldName="feedbackReason">
                      <div className="modern-contact-select-container">
                        <CustomDropdown
                          ref={field.ref}
                          value={field.value}
                          onChange={field.onChange}
                          error={form.formState.errors.feedbackReason}
                        />
                        <FormMessage />
                      </div>
                    </FormControlWithFocus>
                  </FormItem>
                )}
              />

              {/* Subject Section */}
              <FormField
                control={form.control}
                name="subject"
                render={({ field }) => (
                  <FormItem className="modern-contact-section">
                    <FormLabel className="modern-contact-label">
                      Subject
                    </FormLabel>
                    <FormControl>
                      <div className={`modern-contact-subject-wrapper ${!getFeedbackReason ? 'no-prefix' : ''}`}>
                        {getFeedbackReason && (
                          <span className="modern-contact-subject-prefix">
                            {getFeedbackReason}
                          </span>
                        )}
                        <input
                          type="text"
                          className="modern-contact-input modern-contact-subject-input"
                          placeholder="Brief description of your issue"
                          value={userSubject}
                          onChange={(e) => {
                            setUserSubject(e.target.value);
                          }}
                          onBlur={field.onBlur}
                          ref={field.ref}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Message Section */}
              <FormField
                control={form.control}
                name="message"
                render={({ field }) => (
                  <FormItem className="modern-contact-section">
                    <FormLabel className="modern-contact-label">
                      Message
                    </FormLabel>
                    <FormControl>
                      <textarea
                        rows="6"
                        className="modern-contact-textarea"
                        placeholder="Please provide details about your issue..."
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Email Section */}
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="modern-contact-section">
                    <FormLabel className="modern-contact-label">
                      Email Address
                    </FormLabel>
                    <FormControl>
                      <input
                        type="email"
                        autoComplete="new-password"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck="false"
                        className="modern-contact-input"
                        placeholder="your@email.com"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                    <FormDescription className="modern-contact-email-note">
                      We'll use this email to contact you about your issue
                    </FormDescription>
                  </FormItem>
                )}
              />

              <FileUpload
                attachments={fileState.attachments}
                isUploading={fileState.isUploading}
                onFileUpload={handleFileUpload}
                onRemoveFile={handleRemoveFile}
              />
              {/* Submit Section */}
              <div className="modern-contact-submit-section">
                <button
                  type="submit"
                  className="modern-contact-submit-btn"
                  disabled={form.formState.isSubmitting || fileState.isUploading}
                >
                  {form.formState.isSubmitting ? (
                    <span className="modern-contact-spinner"></span>
                  ) : fileState.isUploading ? (
                    'Uploading Files...'
                  ) : (
                    'Submit Request'
                  )}
                </button>
              </div>
            </form>
          </Form>

          {/* Company Info - Dynamic based on selection */}
          {form.watch('feedbackReason') && getCompanyInfo()}

          {/* Support Info */}
          <div className="modern-contact-support-info">
            <h3 className="modern-contact-support-title">Other Ways to Contact Us</h3>
            <div className="modern-contact-support-content">
              <a
                className='modern-contact-support-item'
                href="mailto:support@juicyhouse.com?subject=Contact%20Support"
              >
                <i className="fa-solid fa-envelope modern-contact-support-icon"></i>
                <span className="modern-contact-support-text">
                  {CONFIG.SUPPORT_EMAIL}
                </span>
              </a>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

// PropTypes
ModernContactUs.propTypes = {
  history: PropTypes.object.isRequired,
};

export default withRouter(React.memo(ModernContactUs)); 